import { ArrowRight, Check } from "lucide-react";
import Link from "next/link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Plan } from "./types";

interface PlanCardProps {
  plan: Plan;
}

export function PlanCard(props: PlanCardProps) {
  return (
    <div
      className={cn(
        "flex flex-col rounded-2xl border bg-card p-8 md:px-12",
        props.plan.featured ? "border-primary dark:border-brand-accent md:!py-14" : "border-border",
      )}
    >
      <div>
        <h3 className="flex items-center text-lg font-bold leading-none tracking-tight text-foreground">
          {props.plan.title}
          {props.plan.featured && (
            <Badge
              variant="outline"
              className="ml-4 border-primary/30 bg-primary/10 text-primary dark:border-brand-accent/30 dark:bg-brand-accent/10 dark:text-brand-accent"
            >
              Most popular
            </Badge>
          )}
        </h3>
        {props.plan.audience && (
          <p className="mt-1 text-sm text-muted-foreground">{props.plan.audience}</p>
        )}
        <p className="mt-2 text-3xl font-medium tracking-tight text-foreground md:text-4xl">
          {typeof props.plan.price === "number" ? (
            <>
              ${props.plan.price}
              <span className="relative bottom-0.5 text-base font-normal text-muted-foreground">
                /month
              </span>
            </>
          ) : (
            props.plan.price
          )}
        </p>
        {typeof props.plan.price === "number" && props.plan.interval === "year" && (
          <p className="text-xs text-muted-foreground">billed annually</p>
        )}
        <p className="mt-1 text-sm text-muted-foreground">
          {props.plan.subtitle.split("\n").map((t, idx) => (
            <span className={cn(idx > 0 && "mt-0.5 text-xs", "block")} key={t}>
              {t}
            </span>
          ))}
        </p>
      </div>

      <ul className="mt-7 space-y-2.5 text-sm">
        {props.plan.features.map((feature) => (
          <li key={feature} className="flex items-start gap-2.5">
            <Check
              className="mt-0.5 size-5 shrink-0 text-green-600 dark:text-green-500"
              strokeWidth={3}
            />
            <span className="text-foreground">{feature}</span>
          </li>
        ))}
      </ul>

      <Button
        size="xl"
        variant={props.plan.featured ? "default" : "outline"}
        className="group mt-5 w-full"
        asChild
      >
        <Link href={props.plan.href}>
          {props.plan.cta}
          {props.plan.featured && (
            <ArrowRight className="relative ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          )}
        </Link>
      </Button>
      <p className="mt-2 text-center text-xs text-muted-foreground">{props.plan.pricingMessage}</p>
    </div>
  );
}
